import { Injectable, inject, signal, OnDestroy } from "@angular/core";
import {
  from,
  Observable,
  map,
  of,
  Subject,
  switchMap,
  defer,
  merge,
  takeUntil,
  tap,
  combineLatest,
  BehaviorSubject,
  distinctUntilChanged,
  retryWhen,
  delay,
  take,
} from "rxjs";
import { DataService } from "../data.service";
import { db } from "src/app/db.service";
import { liveQuery } from "dexie";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { IApiResult } from "../shared/models/apiresult.interface";
import moment from "moment";
import { BankDialogComponent } from "./bank-dialog/bank-dialog.component";
import { SalaryDialogComponent } from "./salary-dialog/salary-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: "root",
})
export class BankService implements OnDestroy {
  _dataService = inject(DataService);
  dialog = inject(MatDialog);
  http = inject(HttpClient);
  componentDestroyed$: Subject<boolean> = new Subject();
  selectedCashboxId = this._dataService.getLS("selectedCashboxId")
    ? signal(this._dataService.getLS("selectedCashboxId"))
    : signal(0);
  _selectedCashboxId$ = new BehaviorSubject(this.selectedCashboxId());
  dbUpdated: boolean = false;
  _dbUpdating$ = new BehaviorSubject(true);
  dataLoading = signal(true);
  totalIn = this._dataService.getLS("bankTotalIn") || 0;
  totalOut = this._dataService.getLS("bankTotalOut") || 0;
  jwtToken = signal(this._dataService.getLS("kapitalToken") || "");
  kapitalBalance = 0;

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  getBankTableData(): Observable<any[]> {
    if (this._dataService.endDate) {
      const localData =
        this.selectedCashboxId() != 100
          ? this._dataService.getLS("bankTableData") || []
          : this._dataService.getLS("kapitalData") || [];
      const localObservable = defer(() => {
        return of(localData);
      });

      const liveQueryObservable = combineLatest([
        this._dataService.dateChanged,
        this._selectedCashboxId$.pipe(distinctUntilChanged()),
        this._dbUpdating$.pipe(distinctUntilChanged()),
      ]).pipe(
        tap(() => {
          setTimeout(() => {
            this.dataLoading.set(true);
          }, 100);
        }),
        switchMap(([dateChanged, selectedCashboxId, dbUpdating]) =>
          from(
            liveQuery(() =>
              this.getTransactions(
                this._dataService.startDate.value.format("YYYY-MM-DD"),
                this._dataService.endDate.value.format("YYYY-MM-DD"),
                selectedCashboxId
              )
            )
          ).pipe(
            map((things) => {
              const groupedThings = things.reduce((groups, thing) => {
                const key = thing.transactionId.id;
                const customer =
                  this._dataService
                    .customers()
                    .find((c) => c.id === thing.customerId) ||
                  this._dataService
                    .cashBox()
                    .find((c) => c.id === thing.cashboxId);
                const bankGroup = this._dataService
                  .bankGroups()
                  .find((b) => b.id === thing.bankGroupId);

                if (!groups[key]) {
                  groups[key] = {
                    ...thing,
                    bankGroup: bankGroup ? bankGroup.name : " ",
                    customerName: customer ? customer?.name : " ",
                    dateAdded: thing.transactionId.dateAdded,
                    details: thing.transactionId.details,
                    transactionId: thing.transactionId,
                    transaction: thing.transactionId,
                  };
                } else {
                  if (
                    thing.transactionId?.type == "Salary" ||
                    thing.transactionId?.type == "Cashbox Transfer"
                  ) {
                    groups[key].customerName += thing.customerId
                      ? ", " +
                        this._dataService
                          .customers()
                          .find((c) => c.id === thing.customerId)?.name
                      : " <> " +
                        this._dataService
                          .cashBox()
                          .find((c) => c.id === thing.cashboxId)?.name;
                  } else {
                    groups[key].customerName = this._dataService
                      .customers()
                      .find((c) => c.id === thing.customerId)?.name;
                  }
                  groups[key].outgoings += thing.outgoings;
                }

                return groups;
              }, {});

              return Object.values(groupedThings);
            }),
            map((data) => {
              if (selectedCashboxId === 100) {
                return this.kapitalGetData().pipe(
                  map(
                    (response) => response.responseData.operations.statementList
                  ),
                  map((data) => {
                    let result = [
                      ...data.map((d) => ({
                        ...d,
                        id: d.trnRefNo,
                        tId: d.trnRefNo,
                        details: d.purpose || "",
                        date: moment(new Date(d.trnDt)).format("YYYY-MM-DD"),
                        dateAdded: moment(new Date(d.trnDt)).format(
                          "YYYY-MM-DD"
                        ),
                        income:
                          d.lcyAmount > 0 ? parseFloat(d.lcyAmount) : null,
                        outgoings: d.lcyAmount < 0 ? -d.lcyAmount : null,
                        bankGroup: 1,
                        customerName: d.contrAccount.split("/")[1] || "",
                      })),
                    ];
                    this._dataService.setLS("kapitalData", result.slice(-100));
                    console.log(result);
                    return result;
                  })
                );
              } else {
                return of(data);
              }
            }),
            switchMap((resultData) => {
              return resultData.pipe();
            }),
            map(
              (
                data: {
                  income: number;
                  outgoings: number;
                  [key: string]: any;
                }[]
              ) => {
                this.totalIn = 0;
                this.totalOut = 0;
                this.totalIn =
                  data.reduce(
                    (acc, value) =>
                      !selectedCashboxId && value.bankGroupId == 30
                        ? acc
                        : acc + value.income,
                    0
                  ) ?? 0;
                this.totalOut =
                  data.reduce(
                    (acc, value) =>
                      !selectedCashboxId && value.bankGroupId == 30
                        ? acc
                        : acc + value.outgoings,
                    0
                  ) ?? 0;
                this.totalIn = this.totalIn || 0;
                this.totalOut = this.totalOut || 0;
                this._dataService.setLS("bankTableData", data.slice(-100));
                this._dataService.setLS("bankTotalIn", this.totalIn);
                this._dataService.setLS("bankTotalOut", this.totalOut);
                return data;
              }
            ),
            tap(() => {
              setTimeout(() => {
                this.dataLoading.set(false);
                // if (this.searchInput && this.searchInput.nativeElement.value) {
                //   this.applyFilter(this.searchInput.nativeElement.value);
                // }
              }, 500);
            }),
            retryWhen((errors) => {
              let retries = 0;
              return errors.pipe(
                switchMap((error) => {
                  if (retries < 5) {
                    retries++;
                    return this.kapitalLogin().pipe(
                      tap((r) => {
                        this.jwtToken.set(r.responseData.jwttoken);
                        this._dataService.setLS(
                          "kapitalToken",
                          this.jwtToken()
                        );
                      }),
                      delay(1000), // Add a delay before retrying
                      take(1) // Ensure that the login call is executed only once
                    );
                  } else {
                    throw error; // If max retries reached, propagate the error
                  }
                })
              );
            })
          )
        ),
        takeUntil(this.componentDestroyed$)
      );

      return merge(localObservable, liveQueryObservable);
    }
  }

  getTotals(startDate: Date, endDate: Date, cashboxId?: number) {
    const localData = this._dataService.getLS("bankTotals");
    const localObservable = defer(() => of(localData));

    const dexieObservable = combineLatest([
      this._dataService.dateChanged,
      this._selectedCashboxId$.pipe(distinctUntilChanged()),
      this._dbUpdating$.pipe(distinctUntilChanged()),
    ]).pipe(
      tap(() => setTimeout(() => this.dataLoading.set(true), 100)),
      switchMap(([dateChanged, selectedCashboxId, dbUpdating]) =>
        from(
          liveQuery(() =>
            this.getTotalsHelper(
              this._dataService.endDate.value.format("YYYY-MM-DD"),
              selectedCashboxId
            )
          )
        )
      ),
      map((data) => {
        let result = {
          in: 0,
          out: 0,
          residue: 0,
          outBalance: 0,
          inBalance: 0,
          residueBalance: 0,
          sells: 0,
        };

        for (const value of data) {
          const bankGroup = this._dataService
            .bankGroups()
            .find(
              (b) => b.id.toString() === (value.bankGroupId?.toString() || "")
            );

          if (value.date >= startDate) {
            result.in += value.income;
            result.out += value.outgoings;
            if (bankGroup?.includeInBalance) {
              result.outBalance += value.outgoings;
              result.inBalance += value.income;
            }
            if (value.bankGroupId === 0) result.sells += value.income;
          } else {
            result.residue += value.income - value.outgoings;
            if (bankGroup?.includeInBalance) {
              result.residueBalance += value.income - value.outgoings;
            }
          }
        }

        this._dataService.setLS("bankTotals", result);
        return result;
      })
    );

    return merge(localObservable, dexieObservable);
  }

  openBankDialog(
    type?: string,
    transactionId?: number,
    rowData?,
    event?,
    editMode?: boolean,
    copyMode?: boolean
  ) {
    if ((event && event.event.target.innerText != "more_horiz") || !event) {
      this._dataService.showContextMenu = false;

      if (!rowData?.bankGroup && transactionId) {
        // this.openTransferBankDialog(rowData);
        return;
      }
      if (rowData && rowData.transaction?.type == "Salary") {
        this.dialog.open(SalaryDialogComponent, {
          width: "800px",
          maxHeight: "700px",
          data: {
            cashboxId: this.selectedCashboxId(),
            transactionId: transactionId,
          },
          closeOnNavigation: true,
          panelClass: "materialDialog",
        });
        return;
      }
      let dialogData: {
        type?: string;
        data?: Object;
        cashboxId?: number;
        editMode?: boolean;
        copyMode?: boolean;
      } = {};
      dialogData.type = type;
      dialogData.cashboxId = this.selectedCashboxId();

      if (rowData) {
        rowData.bankGroupName = rowData.bankGroup;
        rowData.transactionId = rowData.tId;
        dialogData.type = rowData.income ? "medaxil" : "";
        dialogData.cashboxId = rowData.cashboxId || 1;
        dialogData.data = rowData;
        dialogData.editMode = editMode;
        dialogData.copyMode = copyMode;
      }

      this.dialog.open(BankDialogComponent, {
        width: "800px",
        maxHeight: "700px",
        data: dialogData,
        closeOnNavigation: true,
        disableClose: this._dataService.mobileDevice
          ? false
          : editMode || copyMode || !transactionId
          ? true
          : false,
        panelClass: "materialDialog",
      });
    }
  }

  openSalaryDialog(data?) {
    this._dataService.showContextMenu = false;
    let dialogData = data
      ? data
      : {
          cashboxId: this.selectedCashboxId(),
        };
    this.dialog.open(SalaryDialogComponent, {
      width: "800px",
      maxHeight: "700px",
      data: dialogData,
      panelClass: "materialDialog",
      closeOnNavigation: true,
      disableClose: this._dataService.mobileDevice
        ? false
        : data
        ? false
        : true,
    });
    // dialogRef.afterClosed().subscribe((res) => {
    //   // this.getDxData();
    //   if (res) {
    //     // console.log(res);
    //     // db.bank.add(res, res.id);
    //   }
    // });
  }

  async getTotalsHelper(endDate, cashboxId?) {
    let result = await db.bank.where("date").belowOrEqual(endDate).toArray();
    if (cashboxId) {
      result = result.filter((b) => b.cashboxId == cashboxId);
    } else {
      result = result.filter((b) => b.bankGroupId != 30);
    }
    return result;
  }

  async getOutgoingsByCategory(startDate: Date, endDate: Date) {
    const banks = await db.bank
      .where("date")
      .between(startDate, endDate, true, true)
      .toArray();

    const outGoings: { [key: string]: any } = banks.reduce((acc, bank) => {
      const { bankGroupId, outgoings } = bank;

      acc[bankGroupId] = acc[bankGroupId] || {
        name:
          this._dataService.bankGroups().find((x) => x.id == bankGroupId)
            ?.name ?? "",
        bankGroupId: bankGroupId,
        out: 0,
      };

      acc[bankGroupId].out += outgoings;
      return acc;
    }, {});

    const result = Object.values(outGoings).sort((a, b) => b.out - a.out);

    return result;
  }

  async calculateProfit() {
    let sellsTotal = 0;
    let costTotal = 0;
    let administrativXercler = 0;

    const sells = await db.sells
      .where("date")
      .between(
        this._dataService.startDate.value.format("YYYY-MM-DD"),
        this._dataService.endDate.value.format("YYYY-MM-DD"),
        true,
        true
      )
      .toArray();

    const result = sells.reduce((accumulator, sell) => {
      const { productId, materialId, quantity, price } = sell;
      let calculatedItemCost: number;
      let originalId: string | number | undefined;
      let accumulatorKey: string | undefined;
      let name: string;
      let itemType: "product" | "material" | undefined;

      if (productId) {
        originalId = productId;
        itemType = "product";
        accumulatorKey = `product_${productId}`;
        const productDetails = this._dataService
          .products()
          .find((p) => p.id === productId);
        name = productDetails?.name || "Unknown Product";

        let baseFormulaCostPart = 0;
        let productUzlemeCost = 0;

        if (productDetails) {
          const formula = productDetails.formulaGroupId
            ? this._dataService
                .formulaGroups()
                .find((f) => f.id === productDetails.formulaGroupId)
            : null;
          const formulaCostValue = formula?.cost || 0;

          if (typeof productDetails.weight === "number") {
            baseFormulaCostPart =
              productDetails.width && productDetails.width !== 0
                ? (formulaCostValue * productDetails.weight) /
                  productDetails.width
                : formulaCostValue * productDetails.weight;

            if (
              isNaN(baseFormulaCostPart) ||
              baseFormulaCostPart === undefined
            ) {
              baseFormulaCostPart = 0;
            }
          }

          if (typeof productDetails.uzleme === "number") {
            productUzlemeCost = productDetails.uzleme;
          }
        }

        calculatedItemCost = baseFormulaCostPart + productUzlemeCost;
        if (isNaN(calculatedItemCost)) {
          calculatedItemCost = 0;
        }
      } else if (materialId) {
        originalId = materialId;
        itemType = "material";
        accumulatorKey = `material_${materialId}`;
        const materialDetails = this._dataService
          .materials()
          .find((m) => m.id === materialId);
        name = "material: " + (materialDetails?.name || "Unknown Material");
        calculatedItemCost = materialDetails?.price || 0;
      } else {
        return accumulator;
      }

      if (originalId && accumulatorKey) {
        if (!accumulator[accumulatorKey]) {
          accumulator[accumulatorKey] = {
            id: originalId, // Store the original, non-prefixed ID
            name: name,
            itemType: itemType,
            totalQuantity: 0,
            sellsSum: 0,
            revenue: 0,
            revenuePercentage: 0,
            unitCost: calculatedItemCost,
            costTotalForItem: 0,
          };
        }

        const itemUnitCostInAccumulator = accumulator[accumulatorKey].unitCost;

        accumulator[accumulatorKey].sellsSum += quantity * price;
        accumulator[accumulatorKey].totalQuantity += quantity;

        const profitForThisLine =
          quantity * (price - itemUnitCostInAccumulator);
        accumulator[accumulatorKey].revenue += profitForThisLine;

        accumulator[accumulatorKey].costTotalForItem +=
          quantity * itemUnitCostInAccumulator;

        accumulator[accumulatorKey].sellsSum = parseFloat(
          accumulator[accumulatorKey].sellsSum.toFixed(2)
        );
        accumulator[accumulatorKey].revenue = parseFloat(
          accumulator[accumulatorKey].revenue.toFixed(2)
        );

        if (accumulator[accumulatorKey].sellsSum !== 0) {
          accumulator[accumulatorKey].revenuePercentage = parseFloat(
            (
              (accumulator[accumulatorKey].revenue /
                accumulator[accumulatorKey].sellsSum) *
              100
            ).toFixed(2)
          );
        } else {
          accumulator[accumulatorKey].revenuePercentage = 0;
        }
      }
      return accumulator;
    }, {} as any);

    const finalResult: any[] = Object.values(result);

    finalResult.forEach((item) => {
      sellsTotal += item.sellsSum;
      costTotal += item.costTotalForItem;
    });

    const bankXercGroups = this._dataService
      .bankGroups()
      .filter((category) => category.bankGroupCategory == 2)
      .map((category) => category.id);

    const bankTransactions = await db.bank
      .where("date")
      .between(
        this._dataService.startDate.value.format("YYYY-MM-DD"),
        this._dataService.endDate.value.format("YYYY-MM-DD"),
        true,
        true
      )
      .toArray();

    const bank = bankTransactions.filter(
      (t) => t.bankGroupId && bankXercGroups.includes(t.bankGroupId)
    );

    bank.forEach((t) => {
      if (typeof t.outgoings === "number") {
        administrativXercler += t.outgoings;
      }
      if (typeof t.income === "number") {
        administrativXercler -= t.income;
      }
    });

    return { sellsTotal, costTotal, administrativXercler };
  }

  async getTransactions(startDate: Date, endDate: Date, selectedCashboxId?) {
    let result;
    if (selectedCashboxId) {
      result = await db.bank
        .where("date")
        .between(startDate, endDate, true, true)
        .toArray()
        .then((transaction) =>
          transaction.filter((t) => t.cashboxId == selectedCashboxId)
        );
    } else {
      result = await db.bank
        .where("date")
        .between(startDate, endDate, true, true)
        .toArray();
    }
    return result;
  }

  kapitalLogin() {
    return this.http.post<IApiResult>(
      "https://cb.kapitalbank.az/api/b2b/login",
      {
        username: environment.kapital.username,
        password: environment.kapital.pass,
      }
    );
  }

  kapitalGetData() {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.jwtToken()}`,
    });
    return this.http.get<IApiResult>(
      `https://cb.kapitalbank.az/api/b2b/v2/statement/account?accountNumber=${
        environment.kapital.accountNo
      }&fromDate=${this._dataService.startDate.value.format(
        "DD-MM-YYYY"
      )}&toDate=${this._dataService.endDate.value.format("DD-MM-YYYY")}`,
      {
        headers,
      }
    );
  }
}
